import clsx from 'clsx';
import { BiUser } from 'react-icons/bi';

import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';

import s from './Avatar.module.scss';

export interface AvatarProps {
  src?: null | string;
  /** Stands in while a private `src` is still resolving, and if it never does. */
  fallbackSrc?: null | string;
  /**
   * The vessel a private `src` belongs to, sent as `X-Service-Id` — a vessel image is
   * refused without it. A person's avatar needs nothing here.
   */
  serviceId?: string;
  firstName?: string;
  lastName?: string;
  size?: number;
  alt?: string;
  className?: string;
}

const initialsOf = (firstName?: string, lastName?: string) =>
  `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();

export const Avatar = ({
  src,
  fallbackSrc,
  serviceId,
  firstName,
  lastName,
  size = 58,
  alt = 'Avatar',
  className,
}: AvatarProps) => {
  // Backend files are private: the link only answers to the bearer token, which a plain
  // `<img src>` cannot attach. The hook resolves those to a `blob:` URL and passes anything
  // else — a bundled fallback asset — straight through.
  const { url } = useAuthorizedFileUrl(src, { serviceId });
  const resolved = url ?? fallbackSrc;

  const initials = initialsOf(firstName, lastName);
  const style = { width: size, height: size, fontSize: size * 0.4 };

  if (resolved) {
    return <img src={resolved} alt={alt} className={clsx(s.avatar, className)} style={style} />;
  }

  if (initials) {
    return (
      <div className={clsx(s.avatarInitials, className)} style={style}>
        {initials}
      </div>
    );
  }

  return (
    <div className={clsx(s.avatarPlaceholder, className)} style={style}>
      <BiUser size={size * 0.55} color="var(--color-slate-80)" />
    </div>
  );
};
